import React from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import TrackChangesOutlinedIcon from '@mui/icons-material/TrackChangesOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import PageHero from '../../components/PageHero';
import { Reveal, RevealGroup, RevealItem } from '../../components/Reveal';
import '../css/QualityAssurance.css';

const QualityAssurance = () => {
  const certifications = [
    { name: 'ISO 9001:2015', description: 'Quality Management System' },
    { name: 'ISO 14001:2015', description: 'Environmental Management' },
    { name: 'OHSAS 18001', description: 'Occupational Health & Safety' },
    { name: 'CE Certification', description: 'European Conformity' },
  ];

  const qualitySteps = [
    { title: 'Raw Material Inspection', description: 'All raw materials undergo rigorous testing before use.' },
    { title: 'In-Process Quality Control', description: 'Continuous monitoring during the manufacturing process.' },
    { title: 'Final Product Testing', description: 'Comprehensive testing of finished products.' },
    { title: 'Documentation & Traceability', description: 'Complete record keeping for quality assurance.' },
  ];

  const principles = [
    { icon: <CheckCircleOutlineIcon />, title: 'Quality First', description: 'Never compromise on quality standards and specifications.' },
    { icon: <BoltOutlinedIcon />, title: 'Continuous Improvement', description: 'Always striving to enhance our processes and products.' },
    { icon: <TrackChangesOutlinedIcon />, title: 'Customer Satisfaction', description: 'Meeting and exceeding customer expectations consistently.' },
    { icon: <LockOutlinedIcon />, title: 'Compliance', description: 'Adhering to all international quality standards.' },
  ];

  return (
    <div className="quality-page">
      <PageHero
        eyebrow="Quality Assurance"
        title="Excellence in every product we deliver."
        subtitle="Rigorous quality control processes, state-of-the-art testing facilities, and a dedicated quality team."
      />

      <section className="quality-content">
        <div className="container">
          <Reveal className="intro-section">
            <h2 className="section-title">Our commitment to quality.</h2>
            <p>Quality is at the heart of everything we do. We maintain the highest standards of manufacturing excellence through rigorous quality control processes, state-of-the-art testing facilities, and a dedicated team of quality professionals.</p>
          </Reveal>

          <div className="quality-principles">
            <Reveal className="section-head centered">
              <span className="eyebrow">Quality Principles</span>
              <h2 className="section-title">How we hold the line.</h2>
            </Reveal>
            <RevealGroup className="principles-grid">
              {principles.map((p, i) => (
                <RevealItem key={i} className="principle-card glass-card">
                  <div className="principle-icon">{p.icon}</div>
                  <h3>{p.title}</h3>
                  <p>{p.description}</p>
                </RevealItem>
              ))}
            </RevealGroup>
          </div>

          <div className="quality-process">
            <Reveal className="section-head centered">
              <span className="eyebrow">Our Process</span>
              <h2 className="section-title">Quality control, step by step.</h2>
            </Reveal>
            <RevealGroup className="process-grid">
              {qualitySteps.map((step, index) => (
                <RevealItem key={index} className="quality-step glass-card">
                  <div className="step-badge">{index + 1}</div>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </RevealItem>
              ))}
            </RevealGroup>
          </div>

          <div className="certifications-section">
            <Reveal className="section-head centered">
              <span className="eyebrow">Certifications</span>
              <h2 className="section-title">Standards we hold.</h2>
            </Reveal>
            <RevealGroup className="certifications-grid">
              {certifications.map((cert, index) => (
                <RevealItem key={index} className="certification-card glass-card">
                  <div className="cert-icon"><EmojiEventsOutlinedIcon /></div>
                  <h3>{cert.name}</h3>
                  <p>{cert.description}</p>
                </RevealItem>
              ))}
            </RevealGroup>
          </div>

          <div className="testing-facilities">
            <Reveal className="section-head centered">
              <span className="eyebrow">Testing Facilities</span>
              <h2 className="section-title">Where every tool is proven.</h2>
            </Reveal>
            <div className="facilities-content">
              <Reveal as="div" className="facilities-text">
                <p>Our state-of-the-art testing laboratories are equipped with the latest technology and equipment to ensure comprehensive quality testing. Our facilities include:</p>
                <ul>
                  <li>Advanced Material Testing Lab</li>
                  <li>Performance Testing Equipment</li>
                  <li>Environmental Testing Chambers</li>
                  <li>Quality Measurement Systems</li>
                  <li>Automated Inspection Systems</li>
                </ul>
              </Reveal>
              <Reveal as="div" className="facilities-image" delay={0.15}>
                <img src="/images/homepage/hero-section-5.png" alt="Quality testing laboratory" />
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default QualityAssurance;
